import React, { Fragment, useCallback, useEffect, useState } from 'react';
import { withTranslation } from 'react-i18next';
import { reduxForm, Field, clearFields, change } from 'redux-form';
import { ButtonToolbar, Button, Label, Collapse, Alert } from 'reactstrap';
import PropTypes from 'prop-types';
import { IconButton, Tooltip } from '@material-ui/core';
import { v4 as uuid } from 'uuid';
import AddIcon from '@material-ui/icons/Add';
import MaterialTable from 'material-table';
import validate from './Validate';
import renderField from '../../../shared/components/form/RenderField';
import renderCheckBoxField from '../../../shared/components/form/CheckBox';
import Select from '../../../shared/components/form/Select';
import {
  APPLICATION_PDF,
  CONTRACT,
  MANAGEMENT,
  OBSERVER,
  PAGE_TYPE,
  URL_PDF,
  WITNESS,
} from '../../../shared/utils/constants';
import DropZoneDragAndDrop from '../../../shared/components/form/DropZoneDragAndDrop';
import columns from './ColumnsSignatories';
import { showNotificationAutoDestroy } from '../../../shared/components/ShowNotification';
import materialTableIcons from '../../../shared/utils/materialTableIcons';
import MaterialTableThemeProvider from '../../../shared/components/MaterialTableThemeProvider';

const ContractTypesForm = (props) => {
  const {
    t,
    initialize,
    dispatch,
    dto,
    backHandler,
    handleSubmit,
    signatories,
  } = props;

  const [listOfSignatory, setListOfSignatory] = useState([]);
  const positions = [
    { value: WITNESS, label: t('general.witness') },
    { value: OBSERVER, label: t('general.observer') },
    { value: MANAGEMENT, label: t('general.management') },
  ];
  const [signatory, setSignatory] = useState({ id: 0 });
  const DEFAULT_CONTRACT_FILE = {
    fileId: uuid(),
    id: 1,
    contractTypeId: 0,
    pageOrder: 0,
    name: CONTRACT,
    pageType: PAGE_TYPE.C,
    fileName: null,
    globalFileId: null,
    type: APPLICATION_PDF,
    preview: URL_PDF,
    notExclude: true,
  };

  const validateSignatory = (signatoryToAdd) => {
    const itemFound = listOfSignatory.find(
      (item) =>
        item.signatory.id === signatoryToAdd.signatory.id &&
        item.position === signatoryToAdd.position
    );
    if (itemFound) {
      return false;
    }
    return true;
  };

  const onChangelistOfSignatory = () => {
    if (!signatory.signatory) {
      showNotificationAutoDestroy(
        'warning',
        t('notifications.attention'),
        t('notifications.select-signatory')
      );
    } else if (!signatory.position) {
      showNotificationAutoDestroy(
        'warning',
        t('notifications.attention'),
        t('notifications.select-position')
      );
    } else {
      const updatedList = [...listOfSignatory, signatory];
      const isValid = validateSignatory(signatory);
      if (isValid) {
        setListOfSignatory(updatedList);
      } else {
        showNotificationAutoDestroy(
          'warning',
          t('notifications.attention'),
          t('notifications.selected-signatory-position')
        );
      }
      dispatch(
        clearFields(
          'ContractTypesForm',
          true,
          true,
          ...['signatory', 'position']
        )
      );
      setSignatory({ id: 0 });
    }
  };

  const setDefaultContratFile = useCallback(() => {
    dispatch(
      change('ContractTypesForm', 'listOfPages', [DEFAULT_CONTRACT_FILE])
    );
  }, []);

  const deleteSignatory = (rowData) => {
    const updatedList = [...listOfSignatory];
    const index = updatedList.findIndex(
      (item) => item.tableData.id === rowData.tableData.id
    );
    updatedList.splice(index, 1);
    setListOfSignatory(updatedList);
  };

  const signatoryChange = (field, value) => {
    const updatedSignatory = { ...signatory };
    updatedSignatory[field] = value;
    setSignatory(updatedSignatory);
  };

  useEffect(() => {
    dispatch(change('ContractTypesForm', 'listOfSignatory', listOfSignatory));
  }, [listOfSignatory]);

  useEffect(() => {
    initialize(dto);
    setListOfSignatory(dto.listOfSignatory);
  }, [dto]);

  useEffect(() => {
    if (dto.id === 0) {
      setDefaultContratFile();
    }
  }, []);

  return (
    <Fragment>
      <form className="form" onSubmit={handleSubmit}>
        <div className="form__form-group">
          <span className="form__form-group-label">{t('general.name')}</span>
          <div className="form__form-group-field">
            <Field
              name="name"
              type="text"
              component={renderField}
              maxLength="100"
              placeholder={t(
                'screens.mnt_tipo_contratos.placeholders.input-name'
              )}
            />
          </div>
        </div>
        <div className="form__form-group--2_columns">
          <div className="form__form-group pr-4">
            <span className="form__form-group-label">
              {t('general.signatories')}
            </span>
            <div className="form_form-group-field">
              <Field
                name="signatory"
                type="text"
                component={Select}
                options={signatories}
                onChange={(value) => signatoryChange('signatory', value)}
                placeholder={t(
                  'screens.mnt_tipo_contratos.placeholders.select'
                )}
              />
            </div>
          </div>
          <div className="form__form-group--2_columns d-flex nowrap ">
            <div className="form__form-group">
              <span className="form__form-group-label">
                {t('general.position')}
              </span>
              <div className="form_form-group-field">
                <Field
                  name="position"
                  type="text"
                  component={Select}
                  options={positions}
                  onChange={(pos) => signatoryChange('position', pos.value)}
                  placeholder={t(
                    'screens.mnt_tipo_contratos.placeholders.select'
                  )}
                />
              </div>
            </div>
            <div className="form__form-group-field flex-1 pl-2">
              <Tooltip title={t('general.add-signatory')}>
                <IconButton aria-label="edit" onClick={onChangelistOfSignatory}>
                  <AddIcon />
                </IconButton>
              </Tooltip>
            </div>
          </div>
        </div>
        <MaterialTableThemeProvider>
          <div className="form__form-group">
            <MaterialTable
              data={listOfSignatory}
              icons={materialTableIcons}
              columns={columns(t)}
              actions={[
                {
                  icon: 'delete',
                  tooltip: t('general.tables.delete_registry'),
                  onClick: (evt, rowData) => {
                    deleteSignatory(rowData);
                  },
                },
              ]}
              options={{
                paging: false,
                showTitle: false,
                search: false,
                toolbar: false,
                actionsColumnIndex: 3,
                headerStyle: {
                  zIndex: 0,
                },
              }}
              localization={{
                header: {
                  actions: t('general.tables.actions'),
                },
                body: {
                  emptyDataSourceMessage: t(
                    'general.tables.no-data-to-display'
                  ),
                },
              }}
            />
          </div>
        </MaterialTableThemeProvider>
        <div className="form__form-group">
          <span className="form__form-group-label">{t('general.files')}</span>
          <div className="form__form-group-field">
            <Field
              name="listOfPages"
              type="text"
              component={DropZoneDragAndDrop}
              accept="application/pdf"
              fileExtensions=".PDF"
            />
          </div>
        </div>
        <div className="form__form-group">
          <div className="form__form-group-field">
            <Label className="generic__label"> {t('general.active')} </Label>
            <Field
              name="active"
              component={renderCheckBoxField}
              className="colored-click"
            />
          </div>
        </div>
        <Field
          name="alert"
          component={(values) => (
            <Collapse
              isOpen={values.meta.touched && values.meta.invalid}
              className="width100"
            >
              <div className="form__form-group">
                <div className="form__form-group-field">
                  <div className="text-center width100">
                    <Alert color="danger">
                      {values.meta.error && <span>{values.meta.error}</span>}
                    </Alert>
                  </div>
                </div>
              </div>
            </Collapse>
          )}
        />
        <ButtonToolbar className="form__button-toolbar">
          <Button color="primary" onClick={backHandler}>
            {t('general.back')}
          </Button>
          <Button color="success" type="submit">
            {t('general.save')}
          </Button>
        </ButtonToolbar>
      </form>
    </Fragment>
  );
};

ContractTypesForm.propTypes = {
  t: PropTypes.func.isRequired,
  initialize: PropTypes.func.isRequired,
  dispatch: PropTypes.func.isRequired,
  dto: PropTypes.shape(PropTypes.shape).isRequired,
  backHandler: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  signatories: PropTypes.arrayOf(PropTypes.shape(PropTypes.shape)).isRequired,
};

export default reduxForm({
  form: 'ContractTypesForm',
  validate,
  keepDirtyOnReinitialize: true,
  enableReinitialize: true,
})(withTranslation('common')(ContractTypesForm));
